import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch, getConfigPost } from "../getConfig";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import { DataTable, PageHeader, StatusChip, bpsToPct, errorMessage, listOf, useCloStyles } from "./shared";

type Account = {
  id: number;
  brandId: number;
  settlementPath: "direct" | "network";
  paymentTermsDays: number;
  feeVatBps: number;
  rewardVatBps: number;
  billingEmail: string | null;
  status: "active" | "inactive";
};

type Form = {
  brandId: string;
  settlementPath: string;
  paymentTermsDays: string;
  feeVatPct: string;
  rewardVatPct: string;
  billingEmail: string;
  status: string;
};

const EMPTY_FORM: Form = {
  brandId: "",
  settlementPath: "direct",
  paymentTermsDays: "30",
  feeVatPct: "20",
  rewardVatPct: "0",
  billingEmail: "",
  status: "active",
};

const COLUMNS = [
  { label: "Brand" },
  { label: "Settlement" },
  { label: "Terms (days)", numeric: true },
  { label: "Fee VAT", numeric: true },
  { label: "Reward VAT", numeric: true },
  { label: "Billing email" },
  { label: "Status" },
  { label: "Actions" },
];

const toForm = (a: Account): Form => ({
  brandId: String(a.brandId),
  settlementPath: a.settlementPath,
  paymentTermsDays: String(a.paymentTermsDays),
  feeVatPct: String(a.feeVatBps / 100),
  rewardVatPct: String(a.rewardVatBps / 100),
  billingEmail: a.billingEmail || "",
  status: a.status,
});

const pctToBps = (pct: string) => Math.round(parseFloat(pct) * 100);

const AccountsList = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [editing, setEditing] = useState<Account | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const names = useBrandNames(accounts.map((a) => a.brandId));

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_BILLING_ACCOUNTS, getConfigGet({}));
      setAccounts(listOf(res.data));
    } catch (err) {
      notify(errorMessage(err, "Failed to load billing accounts"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (a: Account) => {
    setEditing(a);
    setForm(toForm(a));
    setOpen(true);
  };

  const set = (key: keyof Form) => (e: React.ChangeEvent<any>) =>
    setForm({ ...form, [key]: e.target.value });

  const validate = (): string | null => {
    if (!editing && !/^\d+$/.test(form.brandId)) return "Pick a brand";
    const terms = Number(form.paymentTermsDays);
    if (!Number.isInteger(terms) || terms < 1 || terms > 120) return "Payment terms must be 1–120 days";
    for (const [label, v] of [["Fee VAT", form.feeVatPct], ["Reward VAT", form.rewardVatPct]]) {
      const n = parseFloat(v);
      if (isNaN(n) || n < 0 || n > 100) return `${label} must be between 0 and 100%`;
    }
    return null;
  };

  const save = async () => {
    const invalid = validate();
    if (invalid) {
      notify(invalid, "warning");
      return;
    }
    const body: any = {
      settlementPath: form.settlementPath,
      paymentTermsDays: Number(form.paymentTermsDays),
      feeVatBps: pctToBps(form.feeVatPct),
      rewardVatBps: pctToBps(form.rewardVatPct),
      billingEmail: form.billingEmail.trim() || null,
      status: form.status,
    };
    try {
      setSaving(true);
      if (editing) {
        await axios.patch(`${ENDPOINTS.CLO_BILLING_ACCOUNTS}/${editing.id}`, body, {
          headers: getConfigPatch(),
        });
        notify(`Billing account for ${brandLabel(names, editing.brandId)} updated`, "info");
      } else {
        await axios.post(
          ENDPOINTS.CLO_BILLING_ACCOUNTS,
          { ...body, brandId: Number(form.brandId) },
          { headers: getConfigPost() }
        );
        notify(`Billing account for ${brandLabel(names, form.brandId)} created`, "info");
      }
      setOpen(false);
      fetchAccounts();
    } catch (err) {
      notify(errorMessage(err, "Failed to save billing account"), "warning");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={classes.main}>
      <PageHeader
        title="Billing Accounts"
        subtitle="One billing account per retailer: settlement path, payment terms and VAT"
        actions={
          <Button variant="contained" color="primary" disableElevation onClick={openCreate}>
            New account
          </Button>
        }
      />

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable
          columns={COLUMNS}
          isEmpty={accounts.length === 0}
          empty="No billing accounts yet. Add one per retailer before its first invoice run."
        >
          {accounts.map((a) => (
            <TableRow key={a.id} hover>
              <TableCell>{brandLabel(names, a.brandId)}</TableCell>
              <TableCell>{a.settlementPath}</TableCell>
              <TableCell align="right">{a.paymentTermsDays}</TableCell>
              <TableCell align="right">{bpsToPct(a.feeVatBps)}</TableCell>
              <TableCell align="right">{bpsToPct(a.rewardVatBps)}</TableCell>
              <TableCell>{a.billingEmail || "—"}</TableCell>
              <TableCell><StatusChip status={a.status} /></TableCell>
              <TableCell>
                <Tooltip title="Edit account">
                  <IconButton size="small" color="primary" onClick={() => openEdit(a)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editing ? `Edit billing account — ${brandLabel(names, editing.brandId)}` : "New billing account"}
        </DialogTitle>
        <DialogContent>
          <div className={classes.dialogContent}>
            {!editing && (
              <BrandPicker value={form.brandId} onChange={(id) => setForm({ ...form, brandId: id })} required />
            )}
            <TextField select variant="outlined" size="small" label="Settlement path" value={form.settlementPath} onChange={set("settlementPath")}>
              <MenuItem value="direct">direct</MenuItem>
              <MenuItem value="network">network</MenuItem>
            </TextField>
            <TextField
              variant="outlined"
              size="small"
              label="Payment terms"
              value={form.paymentTermsDays}
              onChange={set("paymentTermsDays")}
              type="number"
              inputProps={{ min: 1, max: 120 }}
              InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            />
            <TextField
              variant="outlined"
              size="small"
              label="Fee VAT"
              value={form.feeVatPct}
              onChange={set("feeVatPct")}
              type="number"
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <TextField
              variant="outlined"
              size="small"
              label="Reward VAT"
              value={form.rewardVatPct}
              onChange={set("rewardVatPct")}
              type="number"
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
            <TextField variant="outlined" size="small" label="Billing email" value={form.billingEmail} onChange={set("billingEmail")} type="email" />
            <TextField select variant="outlined" size="small" label="Status" value={form.status} onChange={set("status")}>
              <MenuItem value="active">active</MenuItem>
              <MenuItem value="inactive">inactive</MenuItem>
            </TextField>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} color="primary" variant="contained" disableElevation disabled={saving}>
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification />
    </div>
  );
};

export default AccountsList;
